import CloseIcon from '@mui/icons-material/Close';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { isArray, isEmpty, join, map, size } from 'es-toolkit/compat';
import { PlotlyFigure } from 'interfaces/agentSession';
import React from 'react';

// @ts-ignore
import plotly from 'plotly.js/dist/plotly';
import CopyableChartCaption from './CopyableChartCaption';
import ThemedPlotlyFigure from './ThemedPlotlyFigure';

export interface PlotlyChartActionsProps {
  chartEl: HTMLElement | null;
  figure: PlotlyFigure;
  fileName?: string;
  title: string;
  description?: string | null;
}

const SAFE_FILENAME_REGEX = /[^a-zA-Z0-9_\- ]/g;
const WHITESPACE_REGEX = /\s+/g;
const DEFAULT_EXPORT_WIDTH = 1200;
const DEFAULT_EXPORT_HEIGHT = 800;
const FULLSCREEN_CHART_HEIGHT = 'calc(100vh - 190px)';
const QUOTE_REGEX = /"/g;
const PLOTLY_DTYPE_PREFIX_REGEX = /^[<>=|]/;

type CsvSeriesValue = string | number | boolean | bigint | null | undefined;

const PLOTLY_BINARY_ARRAY_READERS: Record<
  string,
  {
    bytesPerValue: number;
    readValue: (view: DataView, offset: number, littleEndian: boolean) => number | bigint;
  }
> = {
  i1: { bytesPerValue: 1, readValue: (view, offset) => view.getInt8(offset) },
  int8: { bytesPerValue: 1, readValue: (view, offset) => view.getInt8(offset) },
  u1: { bytesPerValue: 1, readValue: (view, offset) => view.getUint8(offset) },
  uint8: { bytesPerValue: 1, readValue: (view, offset) => view.getUint8(offset) },
  i2: { bytesPerValue: 2, readValue: (view, offset, littleEndian) => view.getInt16(offset, littleEndian) },
  int16: { bytesPerValue: 2, readValue: (view, offset, littleEndian) => view.getInt16(offset, littleEndian) },
  u2: { bytesPerValue: 2, readValue: (view, offset, littleEndian) => view.getUint16(offset, littleEndian) },
  uint16: { bytesPerValue: 2, readValue: (view, offset, littleEndian) => view.getUint16(offset, littleEndian) },
  i4: { bytesPerValue: 4, readValue: (view, offset, littleEndian) => view.getInt32(offset, littleEndian) },
  int32: { bytesPerValue: 4, readValue: (view, offset, littleEndian) => view.getInt32(offset, littleEndian) },
  u4: { bytesPerValue: 4, readValue: (view, offset, littleEndian) => view.getUint32(offset, littleEndian) },
  uint32: { bytesPerValue: 4, readValue: (view, offset, littleEndian) => view.getUint32(offset, littleEndian) },
  f4: { bytesPerValue: 4, readValue: (view, offset, littleEndian) => view.getFloat32(offset, littleEndian) },
  float32: { bytesPerValue: 4, readValue: (view, offset, littleEndian) => view.getFloat32(offset, littleEndian) },
  f8: { bytesPerValue: 8, readValue: (view, offset, littleEndian) => view.getFloat64(offset, littleEndian) },
  float64: { bytesPerValue: 8, readValue: (view, offset, littleEndian) => view.getFloat64(offset, littleEndian) },
  i8: { bytesPerValue: 8, readValue: (view, offset, littleEndian) => view.getBigInt64(offset, littleEndian) },
  int64: { bytesPerValue: 8, readValue: (view, offset, littleEndian) => view.getBigInt64(offset, littleEndian) },
  u8: { bytesPerValue: 8, readValue: (view, offset, littleEndian) => view.getBigUint64(offset, littleEndian) },
  uint64: { bytesPerValue: 8, readValue: (view, offset, littleEndian) => view.getBigUint64(offset, littleEndian) },
};

const sanitizeFilename = (name: string) =>
  name.replace(SAFE_FILENAME_REGEX, '').replace(WHITESPACE_REGEX, '_') || 'chart';

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getChartExportSize = (chartEl: HTMLElement) => {
  const { width, height } = chartEl.getBoundingClientRect();
  const fullLayout = (
    chartEl as HTMLElement & {
      _fullLayout?: { width?: number; height?: number };
    }
  )._fullLayout;

  return {
    width: Math.max(Math.round(width || 0), Math.round(fullLayout?.width || 0), DEFAULT_EXPORT_WIDTH),
    height: Math.max(Math.round(height || 0), Math.round(fullLayout?.height || 0), DEFAULT_EXPORT_HEIGHT),
  };
};

const dataUrlToBlob = async (dataUrl: string) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const decodePlotlyBinarySeries = (series: { bdata: string; dtype: string }): CsvSeriesValue[] => {
  const rawDtype = series.dtype.trim().toLowerCase();
  const normalizedDtype = rawDtype.replace(PLOTLY_DTYPE_PREFIX_REGEX, '');
  const binaryArrayReader = PLOTLY_BINARY_ARRAY_READERS[normalizedDtype];

  if (!binaryArrayReader) return [];

  const littleEndian = rawDtype[0] !== '>';
  const binaryString = atob(series.bdata);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const valueCount = Math.floor(bytes.byteLength / binaryArrayReader.bytesPerValue);

  return Array.from({ length: valueCount }, (_, index) =>
    binaryArrayReader.readValue(view, index * binaryArrayReader.bytesPerValue, littleEndian)
  );
};

const normalizeTraceSeries = (series: unknown): CsvSeriesValue[] => {
  if (isArray(series)) return series;
  if (ArrayBuffer.isView(series)) return Array.from(series as unknown as ArrayLike<number | bigint>);
  if (series instanceof ArrayBuffer) return Array.from(new Uint8Array(series));

  if (!series || typeof series !== 'object') return [];

  const { bdata, data, dtype } = series as {
    bdata?: unknown;
    data?: unknown;
    dtype?: unknown;
  };

  if (typeof bdata === 'string' && typeof dtype === 'string') {
    return decodePlotlyBinarySeries({ bdata, dtype });
  }

  if (data) return normalizeTraceSeries(data);

  return [];
};

const getLayoutAxisKey = (axisLetter: 'x' | 'y', axisReference: unknown): string => {
  if (typeof axisReference !== 'string' || axisReference.trim() === '' || axisReference === axisLetter) {
    return `${axisLetter}axis`;
  }

  const suffix = axisReference.slice(axisLetter.length);
  return `${axisLetter}axis${suffix}`;
};

const getAxisTitleText = (axis: unknown): string | null => {
  if (!axis || typeof axis !== 'object') return null;

  const axisTitle = (axis as { title?: unknown }).title;
  if (typeof axisTitle === 'string') {
    const trimmedTitle = axisTitle.trim();
    return trimmedTitle || null;
  }

  const axisTitleText = (axisTitle as { text?: unknown } | undefined)?.text;
  if (typeof axisTitleText !== 'string') return null;

  const trimmedTitle = axisTitleText.trim();
  return trimmedTitle || null;
};

const getTraceAxisHeader = (figure: PlotlyFigure, trace: Record<string, unknown>, axisLetter: 'x' | 'y'): string => {
  const layoutAxisKey = getLayoutAxisKey(axisLetter, trace[axisLetter === 'x' ? 'xaxis' : 'yaxis']);
  const axisTitle = getAxisTitleText(figure.layout?.[layoutAxisKey]);

  return axisTitle || axisLetter.toUpperCase();
};

const renderChartImage = async (chartEl: HTMLElement, format: 'png' | 'svg') => {
  const { width, height } = getChartExportSize(chartEl);

  return plotly.toImage(chartEl, {
    format,
    width,
    height,
    scale: format === 'png' ? 2 : 1,
  });
};

const tracesToCsv = (figure: PlotlyFigure): string => {
  const traces = figure.data || [];
  if (traces.length === 0) return '';

  const rows: string[][] = [];

  for (const trace of traces) {
    const traceName = String(trace.name || '');
    const xHeader = getTraceAxisHeader(figure, trace, 'x');
    const yHeader = getTraceAxisHeader(figure, trace, 'y');
    const x = normalizeTraceSeries(trace.x);
    const y = normalizeTraceSeries(trace.y);
    const labels = normalizeTraceSeries(trace.labels);
    const values = normalizeTraceSeries(trace.values);

    if (size(labels) > 0 && size(values) > 0) {
      if (isEmpty(rows)) rows.push(['Trace', 'Label', 'Value']);
      for (let index = 0; index < size(labels); index += 1) {
        rows.push([traceName, String(labels[index] ?? ''), String(values[index] ?? '')]);
      }
    } else if (size(x) > 0 || size(y) > 0) {
      if (isEmpty(rows)) rows.push(['Trace', xHeader, yHeader]);
      const rowCount = Math.max(size(x), size(y));
      for (let index = 0; index < rowCount; index += 1) {
        rows.push([traceName, String(x[index] ?? ''), String(y[index] ?? '')]);
      }
    }
  }

  return join(
    map(rows, (row) =>
      join(
        map(row, (cell) => `"${cell.replace(QUOTE_REGEX, '""')}"`),
        ','
      )
    ),
    '\n'
  );
};

const PlotlyChartActions: React.FC<PlotlyChartActionsProps> = ({ chartEl, figure, fileName, title, description }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [fullscreenChartEl, setFullscreenChartEl] = React.useState<HTMLElement | null>(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = React.useState(false);
  const [menuSource, setMenuSource] = React.useState<'inline' | 'fullscreen'>('inline');
  const filename = sanitizeFilename(fileName || title);
  const open = Boolean(anchorEl);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const getCurrentChartEl = React.useCallback(
    () => (menuSource === 'fullscreen' ? fullscreenChartEl || chartEl : chartEl),
    [chartEl, fullscreenChartEl, menuSource]
  );

  const handleOpenActionsMenu = (source: 'inline' | 'fullscreen') => (event: React.MouseEvent<HTMLElement>) => {
    setMenuSource(source);
    setAnchorEl(event.currentTarget);
  };

  const handleOpenFullscreen = () => {
    setFullscreenChartEl(chartEl);
    setIsFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreenOpen(false);
    setFullscreenChartEl(null);
    handleCloseMenu();
  };

  const handleDownloadPng = async () => {
    handleCloseMenu();
    const currentChartEl = getCurrentChartEl();
    if (!currentChartEl) return;

    try {
      const imageDataUrl = await renderChartImage(currentChartEl, 'png');
      const blob = await dataUrlToBlob(imageDataUrl);
      downloadBlob(blob, `${filename}.png`);
    } catch (error) {
      console.error('Failed to export chart as PNG', error);
    }
  };

  const handleDownloadSvg = async () => {
    handleCloseMenu();
    const currentChartEl = getCurrentChartEl();
    if (!currentChartEl) return;

    try {
      const imageDataUrl = await renderChartImage(currentChartEl, 'svg');
      const blob = await dataUrlToBlob(imageDataUrl);
      downloadBlob(blob, `${filename}.svg`);
    } catch (error) {
      console.error('Failed to export chart as SVG', error);
    }
  };

  const handleCopyToClipboard = async () => {
    handleCloseMenu();
    const currentChartEl = getCurrentChartEl();

    if (!currentChartEl || !navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
      return;
    }

    try {
      const imageDataUrl = await renderChartImage(currentChartEl, 'png');
      const blob = await dataUrlToBlob(imageDataUrl);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    } catch (error) {
      console.error('Failed to copy chart image to clipboard', error);
    }
  };

  const handleDownloadCsv = () => {
    handleCloseMenu();
    const csv = tracesToCsv(figure);
    if (!csv) return;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `${filename}.csv`);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpenFullscreen}
        aria-label="Open full screen"
        disabled={isEmpty(figure.data)}
      >
        <FullscreenRoundedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={handleOpenActionsMenu('inline')}
        aria-label="Chart actions"
        disabled={isEmpty(figure.data)}
      >
        <IosShareRoundedIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        <MenuItem onClick={handleDownloadPng} disabled={!getCurrentChartEl()}>
          <ListItemIcon>
            <ImageRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PNG</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadSvg} disabled={!getCurrentChartEl()}>
          <ListItemIcon>
            <FileDownloadRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download SVG</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyToClipboard} disabled={!getCurrentChartEl()}>
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy to clipboard</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadCsv}>
          <ListItemIcon>
            <TableChartRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download data as CSV</ListItemText>
        </MenuItem>
      </Menu>

      {isFullscreenOpen && (
        <Dialog
          open
          onClose={handleCloseFullscreen}
          fullScreen
          sx={{
            '& .MuiDialog-paper': {
              bgcolor: 'background.default',
            },
          }}
        >
          <DialogTitle sx={{ pb: description ? 1.5 : 2 }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h2">{title}</Typography>
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={handleOpenActionsMenu('fullscreen')} aria-label="Chart actions">
                  <IosShareRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={handleCloseFullscreen} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
            {description && (
              <Box sx={{ mt: 1.25, maxWidth: 960 }}>
                <CopyableChartCaption caption={description} variant="body2" color="text.secondary" />
              </Box>
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ width: '100%', height: FULLSCREEN_CHART_HEIGHT }}>
              <ThemedPlotlyFigure figure={figure} height="100%" onPlotlyInit={setFullscreenChartEl} />
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default PlotlyChartActions;
